// src/components/AdminPrototype/AdminPrototype.jsx
import  { useState, useEffect } from 'react';

import { RiRefreshLine } from 'react-icons/ri';
import {  RiLoaderLine, RiCheckLine, RiCloseLine, RiTruckLine } from 'react-icons/ri';
import './AdminPrototype.css';
import { BASE_URL } from '../utils/constants';


const AdminPrototype = () => {
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingPayments: 0,
    pendingDeliveries: 0,
    recentOrders: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${BASE_URL}/api/dashboard/stats`);
      if (!response.ok) {
        throw new Error(`Failed to load dashboard: ${response.status}`);
      }

      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Unable to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();

    // Auto-refresh every 60 seconds (optional)
    const interval = setInterval(fetchDashboard, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    fetchDashboard();
  };

  // Build activity feed from recent orders
  const buildActivityFeed = () => {
    return stats.recentOrders.map(order => {
      const latestChange = order.deliveryStatusHistory?.[order.deliveryStatusHistory.length - 1];
      const timeAgo = new Date(latestChange?.timestamp || order.updatedAt || order.createdAt)
        .toLocaleString([], { hour: '2-digit', minute: '2-digit', hour12: true });

      let title = '';
      let subtitle = '';
      let icon = RiRefreshLine;
      let bg = 'rgba(232, 213, 160, 0.2)';

      if (order.paymentStatus === 'successful') {
        title = `Payment confirmed for Order #${order.reference}`;
        subtitle = `Amount: ZMK ${order.items.reduce((sum, i) => sum + i.totalCost, 0).toFixed(2)} • ${timeAgo}`;
        icon = RiCheckLine;
        bg = 'rgba(203, 227, 195, 0.4)';
      } else if (order.paymentStatus === 'failed') {
        title = `Payment failed - Order #${order.reference}`;
        subtitle = `Phone: ${order.phone} • ${timeAgo}`;
        icon = RiCloseLine;
        bg = 'rgba(244, 209, 198, 0.4)';
      } else if (order.deliveryStatus === 'shipped') {
        title = `Order #${order.reference} shipped`;
        subtitle = `Est. delivery: ${order.estimatedDeliveryDate ? new Date(order.estimatedDeliveryDate).toLocaleDateString() : 'TBD'} • ${timeAgo}`;
        icon = RiTruckLine;
        bg = 'rgba(139, 126, 111, 0.15)';
      } else if (order.deliveryStatus === 'delivered') {
        title = `Order #${order.reference} delivered`;
        subtitle = `To ${order.deliveryAddress?.fullName || 'Customer'} • ${timeAgo}`;
        icon = RiCheckLine;
        bg = 'rgba(203, 227, 195, 0.4)';
      } else {
        title = `Order #${order.reference} created`;
        subtitle = `Pending payment • ${timeAgo}`;
        icon = RiRefreshLine;
        bg = 'rgba(232, 213, 160, 0.2)';
      }

      return { title, subtitle, icon, bg };
    });
  };

  const activityFeed = buildActivityFeed();

  return (
    <div className="admin-prototype-page">
      {/* <Header /> */}

      <main className="dashboard-main">
        <div className="container">
          {error && <div className="error-message">{error}</div>}

          {loading ? (
            <div className="loading">
              <RiLoaderLine className="spin" size={32} /> Loading dashboard...
            </div>
          ) : (
            <>
              <div className="stats-overview">
                <div className="stat-card">
                  <div className="stat-label">Total Orders</div>
                  <div className="stat-value">{stats.totalOrders}</div>
                  <div className="stat-change positive">Updated real-time</div>
                </div>

                <div className="stat-card">
                  <div className="stat-label">Pending Payments</div>
                  <div className="stat-value">{stats.pendingPayments}</div>
                  <div className="stat-change neutral">Awaiting confirmation</div>
                </div>

                <div className="stat-card">
                  <div className="stat-label">Pending Deliveries</div>
                  <div className="stat-value">{stats.pendingDeliveries}</div>
                  <div className="stat-change positive">Active queue</div>
                </div>

                <div className="stat-card warning">
                  <div className="stat-label">Failed Deliveries</div>
                  <div className="stat-value">
                    {stats.recentOrders.filter(o => o.deliveryStatus === 'failed').length}
                  </div>
                  <div className="stat-change negative">Requires attention</div>
                </div>
              </div>

              <div className="content-grid">
                <div className="main-content-area">
                  <div className="card large-card">
                    <div className="card-header">
                      <h2>Today's Performance</h2>
                      <button className="refresh-btn" onClick={handleRefresh}>
                        <RiRefreshLine size={18} /> Refresh
                      </button>
                    </div>
                    <div className="chart-placeholder">
                      [Real-time chart coming soon – Delivery Success Rate: ~94%]
                    </div>
                  </div>

                  <div className="card">
                    <h2>Recent Orders (Last 24h)</h2>
                    <div className="mini-order-list">
                      {stats.recentOrders.length === 0 ? (
                        <p>No recent orders</p>
                      ) : (
                        stats.recentOrders.map(order => (
                          <div key={order.reference} className="mini-order-row">
                            <div className="order-id">#{order.reference}</div>
                            <div className={`order-status ${order.deliveryStatus}`}>
                              {order.deliveryStatus.charAt(0).toUpperCase() + order.deliveryStatus.slice(1)}
                            </div>
                            <div className="order-time">
                              {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="sidebar-activity">
                  <div className="card activity-card">
                    <div className="card-header">
                      <h2>Recent Activity</h2>
                    </div>

                    <div className="activity-feed">
                      {activityFeed.length === 0 ? (
                        <p>No recent activity</p>
                      ) : (
                        activityFeed.map((item, index) => (
                          <div key={index} className="activity-row">
                            <div
                              className="activity-icon-wrapper"
                              style={{ backgroundColor: item.bg }}
                            >
                              <item.icon size={16} />
                            </div>
                            <div className="activity-text">
                              <div className="activity-title">{item.title}</div>
                              <div className="activity-subtitle">{item.subtitle}</div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminPrototype;

// import React from 'react';
// import {
//   RiArchiveLine,       // ← replaced RiPackageLine with this (common package/orders icon)
//   RiRefreshLine,
//   RiRestartLine,
//   RiUserAddLine,
//   RiCheckboxCircleLine,
//   RiAlertLine
// } from 'react-icons/ri';

// import './AdminPrototype.css';

// const AdminPrototype = () => {
//   const activityFeed = [
//     {
//       type: 'refund',
//       title: 'Refund processed for Order #ORD-2024-001823',
//       subtitle: 'Amount: $89.50 • 8 minutes ago',
//       icon: RiRestartLine,
//       bg: 'rgba(232, 213, 160, 1)',
//     },
//     {
//       type: 'courier',
//       title: 'Courier Mike Johnson assigned to 5 new deliveries',
//       subtitle: 'Route: Downtown District • 15 minutes ago',
//       icon: RiUserAddLine,
//       bg: 'rgba(139, 126, 111, 0.1)',
//     },
//     {
//       type: 'batch',
//       title: 'Batch delivery completed - 12 packages delivered',
//       subtitle: 'Courier: Emma Rodriguez • 23 minutes ago',
//       icon: RiCheckboxCircleLine,
//       bg: 'rgba(203, 227, 195, 1)',
//     },
//     {
//       type: 'alert',
//       title: 'Delivery failed - Customer unavailable',
//       subtitle: 'Order #ORD-2024-001842 • 35 minutes ago',
//       icon: RiAlertLine,
//       bg: 'rgba(244, 209, 198, 1)',
//     },
//   ];

//   return (
//     <div className="admin-prototype-page">
//       <header className="admin-header">
//         <div className="logo-section">
//           <div className="logo-icon">
//             <RiArchiveLine size={24} color="white" />  {/* ← updated here */}
//           </div>
//           <span className="logo-text">OrderFlow</span>
//         </div>

//         <nav className="admin-nav">
//           <a href="#dashboard" className="active">Dashboard</a>
//           <a href="#orders">Orders</a>
//           <a href="#profile">Profile</a>
//         </nav>
//       </header>

//       <main className="dashboard-main">
//         <div className="stats-overview">
//           <div className="stat-card">
//             <div className="stat-label">Active Orders</div>
//             <div className="stat-value">142</div>
//             <div className="stat-change positive">+12% from yesterday</div>
//           </div>

//           <div className="stat-card">
//             <div className="stat-label">Pending Fulfillment</div>
//             <div className="stat-value">38</div>
//             <div className="stat-change neutral">Same as yesterday</div>
//           </div>

//           <div className="stat-card">
//             <div className="stat-label">In Transit</div>
//             <div className="stat-value">91</div>
//             <div className="stat-change positive">+8 since morning</div>
//           </div>

//           <div className="stat-card warning">
//             <div className="stat-label">Failed Deliveries (Today)</div>
//             <div className="stat-value">4</div>
//             <div className="stat-change negative">-2 from yesterday</div>
//           </div>
//         </div>

//         <div className="content-grid">
//           <div className="main-content-area">
//             <div className="card large-card">
//               <div className="card-header">
//                 <h2>Today's Performance</h2>
//                 <button className="refresh-btn">
//                   <RiRefreshLine size={18} /> Refresh
//                 </button>
//               </div>
//               <div className="chart-placeholder">
//                 [Placeholder: Delivery Success Rate Chart – 94% today]
//               </div>
//             </div>

//             <div className="card">
//               <h2>Recent Orders (Last 24h)</h2>
//               <div className="mini-order-list">
//                 <div className="mini-order-row">
//                   <div className="order-id">#ORD-2024-001842</div>
//                   <div className="order-status out">Out for Delivery</div>
//                   <div className="order-time">2:15 PM</div>
//                 </div>
//                 <div className="mini-order-row">
//                   <div className="order-id">#ORD-2024-001823</div>
//                   <div className="order-status delivered">Delivered</div>
//                   <div className="order-time">1:40 PM</div>
//                 </div>
//               </div>
//             </div>
//           </div>

//           <div className="sidebar-activity">
//             <div className="card activity-card">
//               <div className="card-header">
//                 <h2>Recent Activity</h2>
//               </div>

//               <div className="activity-feed">
//                 {activityFeed.map((item, index) => (
//                   <div key={index} className="activity-row">
//                     <div
//                       className="activity-icon-wrapper"
//                       style={{ backgroundColor: item.bg }}
//                     >
//                       <item.icon size={16} />
//                     </div>
//                     <div className="activity-text">
//                       <div className="activity-title">{item.title}</div>
//                       <div className="activity-subtitle">{item.subtitle}</div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </div>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default AdminPrototype;
// // import React from 'react';
// // import {
// //   RiPackageLine,
// //   RiRefreshLine,
// //   RiRestartLine,
// //   RiUserAddLine,
// //   RiPackageCheckLine,
// //   RiErrorWarningLine
// // } from 'react-icons/ri';
// // //import { RiPackageLine, RiRefreshCwLine, RiRotateCcwLine, RiUserPlusLine, RiPackageCheckLine, RiAlertTriangleLine } from 'react-icons/ri';
// // import './AdminPrototype.css';

// // const AdminPrototype = () => {
// //   const activityFeed = [
// //     {
// //       type: 'refund',
// //       title: 'Refund processed for Order #ORD-2024-001823',
// //       subtitle: 'Amount: $89.50 • 8 minutes ago',
// //       icon: RiRotateCcwLine,
// //       bg: 'rgba(232, 213, 160, 1)',
// //     },
// //     {
// //       type: 'courier',
// //       title: 'Courier Mike Johnson assigned to 5 new deliveries',
// //       subtitle: 'Route: Downtown District • 15 minutes ago',
// //       icon: RiUserPlusLine,
// //       bg: 'rgba(139, 126, 111, 0.1)',
// //     },
// //     {
// //       type: 'batch',
// //       title: 'Batch delivery completed - 12 packages delivered',
// //       subtitle: 'Courier: Emma Rodriguez • 23 minutes ago',
// //       icon: RiPackageCheckLine,
// //       bg: 'rgba(203, 227, 195, 1)',
// //     },
// //     {
// //       type: 'alert',
// //       title: 'Delivery failed - Customer unavailable',
// //       subtitle: 'Order #ORD-2024-001842 • 35 minutes ago',
// //       icon: RiAlertTriangleLine,
// //       bg: 'rgba(244, 209, 198, 1)',
// //     },
// //   ];

// //   return (
// //     <div className="admin-prototype-page">
// //       <header className="admin-header">
// //         <div className="logo-section">
// //           <div className="logo-icon">
// //             <RiPackageLine size={24} color="white" />
// //           </div>
// //           <span className="logo-text">OrderFlow</span>
// //         </div>

// //         <nav className="admin-nav">
// //           <a href="#dashboard" className="active">Dashboard</a>
// //           <a href="#orders">Orders</a>
// //           <a href="#profile">Profile</a>
// //         </nav>
// //       </header>

// //       <main className="dashboard-main">
// //         <div className="stats-overview">
// //           <div className="stat-card">
// //             <div className="stat-label">Active Orders</div>
// //             <div className="stat-value">142</div>
// //             <div className="stat-change positive">+12% from yesterday</div>
// //           </div>

// //           <div className="stat-card">
// //             <div className="stat-label">Pending Fulfillment</div>
// //             <div className="stat-value">38</div>
// //             <div className="stat-change neutral">Same as yesterday</div>
// //           </div>

// //           <div className="stat-card">
// //             <div className="stat-label">In Transit</div>
// //             <div className="stat-value">91</div>
// //             <div className="stat-change positive">+8 since morning</div>
// //           </div>

// //           <div className="stat-card warning">
// //             <div className="stat-label">Failed Deliveries (Today)</div>
// //             <div className="stat-value">4</div>
// //             <div className="stat-change negative">-2 from yesterday</div>
// //           </div>
// //         </div>

// //         <div className="content-grid">
// //           {/* Left - Main content area */}
// //           <div className="main-content-area">
// //             <div className="card large-card">
// //               <div className="card-header">
// //                 <h2>Today's Performance</h2>
// //                 <button className="refresh-btn">
// //                   <RiRefreshCwLine size={18} /> Refresh
// //                 </button>
// //               </div>
// //               <div className="chart-placeholder">
// //                 [Placeholder: Delivery Success Rate Chart – 94% today]
// //               </div>
// //             </div>

// //             <div className="card">
// //               <h2>Recent Orders (Last 24h)</h2>
// //               <div className="mini-order-list">
// //                 <div className="mini-order-row">
// //                   <div className="order-id">#ORD-2024-001842</div>
// //                   <div className="order-status out">Out for Delivery</div>
// //                   <div className="order-time">2:15 PM</div>
// //                 </div>
// //                 <div className="mini-order-row">
// //                   <div className="order-id">#ORD-2024-001823</div>
// //                   <div className="order-status delivered">Delivered</div>
// //                   <div className="order-time">1:40 PM</div>
// //                 </div>
// //                 {/* ... more rows ... */}
// //               </div>
// //             </div>
// //           </div>

// //           {/* Right sidebar - Activity Feed */}
// //           <div className="sidebar-activity">
// //             <div className="card activity-card">
// //               <div className="card-header">
// //                 <h2>Recent Activity</h2>
// //               </div>

// //               <div className="activity-feed">
// //                 {activityFeed.map((item, index) => (
// //                   <div key={index} className="activity-row">
// //                     <div className="activity-icon-wrapper" style={{ backgroundColor: item.bg }}>
// //                       <item.icon size={16} />
// //                     </div>
// //                     <div className="activity-text">
// //                       <div className="activity-title">{item.title}</div>
// //                       <div className="activity-subtitle">{item.subtitle}</div>
// //                     </div>
// //                   </div>
// //                 ))}
// //               </div>
// //             </div>
// //           </div>
// //         </div>
// //       </main>
// //     </div>
// //   );
// // };

// // export default AdminPrototype;